import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit,} from '@angular/core';
import {OnDestroy$, takeUntilDestroyed} from '@pdtec/ngx-observable-lifecycle';
import {SearchProgressService} from '../../services/search-progress.service';
import {SearchService} from "../../services/search/search.service";
import {APP_CONFIG, AppConfig} from "../../config/app.config";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";
import {environment} from "../../../environments/environment";

@Component({
  selector: 'app-search-progress-linear',
  templateUrl: './search-progress-linear.component.html',
  styleUrls: ['./search-progress-linear.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SearchProgressLinearComponent extends OnDestroy$ implements OnInit, OnDestroy {
  @Input() type: string = 'line';

  public searchStart$ = this.searchProgressService.start$;

  public searchEnd$ = this.searchProgressService.end$;
  public searchHide$ = this.searchProgressService.hide$;

  public shown = false;
  public isFooterVisible = false;
  private footerObserver: IntersectionObserver | null = null;

  public viewProgress = 0;
  public isAnimating  = false;

  public transition;
  public transitionD;

  public marginTop = this.getInitialMarginTop();
  public marginBottom = this.getInitialMarginBottom();
  public opacity = 1;
  public opacityTransition = '0.5s ease';

  public texts: string[] = [];
  public currentText: string = '';
  private textIndex = 0;

  private intervalId: any = null;
  private textIntervalId: any = null;
  private hideTimeoutId: any = null;
  private endWaitIntervalId: any = null;

  constructor(
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
    private searchService: SearchService,
    private searchProgressService: SearchProgressService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    if (environment.mobile && this.type === 'button') {
      const footer = document.querySelector('.mobile-footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.isFooterVisible = entry.isIntersecting;
          this.cdRef.detectChanges();
        });
        this.footerObserver.observe(footer);
      }
    }
    if (this.type === 'line-note') {
      const texts = this.appConfig.aviaProgressBarTexts;
      this.texts = Array.isArray(texts) ? texts : [];
      if (this.texts.length > 0) {
        this.textIndex = this.getRandomTextIndex(this.textIndex);
        this.currentText = this.texts[this.textIndex];
      } else {
        this.currentText = '';
      }
    }
    // Автономный компонент
    this.cdRef.detach();
    this.searchStart$.pipe(takeUntilDestroyed(this)).subscribe(() => {
      this.startSearchProgress();
    });
    this.searchEnd$.pipe(takeUntilDestroyed(this)).subscribe(() => {
      this.endSearch();
    });
    this.searchHide$.pipe(takeUntilDestroyed(this)).subscribe(() => {
      this.hideSearchProgressImmediately();
    });
  }

  ngOnDestroy(): void {
    this.footerObserver?.disconnect();
    this.clearStartSearchTimer();
    this.clearEndSearchTimeout();
    this.clearTextInterval();
  }

  private resetProgress(): void {
    this.viewProgress = 0;
    this.transitionD = '0s';
  }

  /**
   * Анимация начала поиска
   * TODO: в идеале переделать на BrowserAnimation,
   *      чтобы выпилить весь это 3.14дец с таймерами.
   *      С таймерами слишком сложно, напрямую с Animation можно сильно проще
   * @private
   */
  private startSearchProgress(): void {
    this.clearStartSearchTimer();
    this.clearEndSearchTimeout();
    this.clearTextInterval();

    this.shown = true;
    this.opacity = 1;
    this.marginTop = this.getInitialMarginTop();
    this.marginBottom = this.getInitialMarginBottom();
    // При начале поиска не дожидаясь окончания предыдущего
    // Отключаем анимацию и применяем
    this.resetProgress();
    this.cdRef.detectChanges();

    this.isAnimating = true;
    requestAnimationFrame(() => {
      if (!this.shown) {
        return;
      }

      this.transition = 'width';
      this.transitionD = '0.1s';
      this.viewProgress = 1;
      this.cdRef.detectChanges();
    });

    const totalDurationMs = 30000;
    const tickMs = 100;
    const increment = (100 / totalDurationMs) * tickMs;

    this.intervalId = setInterval(() => {
      this.viewProgress = Math.min(this.viewProgress + increment, 100); // Увеличиваем прогресс
      this.cdRef.detectChanges();
    }, tickMs);

    if (this.type === 'line-note') {
      if (this.texts.length > 0) {
        this.textIntervalId = setInterval(() => {
          this.textIndex = this.getRandomTextIndex(this.textIndex);
          this.currentText = this.texts[this.textIndex];
          this.cdRef.detectChanges();
        }, 2000);
      }
    }
  }

  private clearStartSearchTimer(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private endSearch(): void {
    this.clearEndSearchTimeout();
    this.clearTextInterval();
    // Timer чтобы не пропадал прогрессбар при перепоиске (мерцание)
    this.transition = 'width';
    this.transitionD = '2s';
    this.viewProgress = 100;
    this.clearStartSearchTimer();

    if (this.type === 'line-note') {
      this.endWaitIntervalId = setInterval(() => {
        if (this.viewProgress >= 100) {
          if (this.endWaitIntervalId !== null) {
            clearInterval(this.endWaitIntervalId);
            this.endWaitIntervalId = null;
          }

          // когда реально дошло - запускаем fade+move
          this.opacity = 0;
          this.marginTop = this.getHiddenMarginTop();
          this.marginBottom = this.getHiddenMarginBottom();
          this.cdRef.detectChanges();

          // а потом убираем совсем
          this.hideTimeoutId = setTimeout(() => {
            this.shown = false;
            this.resetProgress();
            this.cdRef.detectChanges();
          }, 1000);
        }
      }, 1800);
    } else {
      this.hideTimeoutId = setTimeout(() => {
        this.shown = false;
        this.resetProgress();
        this.cdRef.detectChanges();
      }, 2000);
    }

    this.cdRef.detectChanges();
  }

  private hideSearchProgressImmediately(): void {
    this.clearStartSearchTimer();
    this.clearEndSearchTimeout();
    this.clearTextInterval();
    this.shown = false;
    this.opacity = 1;
    this.isAnimating = false;
    this.resetProgress();
    this.cdRef.detectChanges();
  }

  private clearEndSearchTimeout(): void {
    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
    if (this.endWaitIntervalId !== null) {
      clearInterval(this.endWaitIntervalId);
      this.endWaitIntervalId = null;
    }
  }
  private clearTextInterval(): void {
    if (this.textIntervalId !== null) {
      clearInterval(this.textIntervalId);
      this.textIntervalId = null;
    }
  }

  private getRandomTextIndex(excludeIndex: number): number {
    if (!this.texts || this.texts.length === 0) {
      return 0;
    }
    let index;
    do {
      index = Math.floor(Math.random() * this.texts.length);
    } while (index === excludeIndex && this.texts.length > 1); // избегаем повторений подряд
    return index;
  }

  get searchProgressLabel(): string {
    const isHotelsSearch = !!this.hotelToursPageConfig?.searchFormValues?.hotelsSearch;
    return this.hotelToursPageConfig.translates.searchProgress[isHotelsSearch ? 'hotels' : 'tours'];
  }

  get isDesktopHotelsSearch(): boolean {
    return !environment.mobile && !!this.hotelToursPageConfig?.searchFormValues?.hotelsSearch;
  }

  private getInitialMarginTop(): string {
    return environment.mobile ? '12px' : '0';
  }

  private getInitialMarginBottom(): string {
    return environment.mobile ? '0' : '12px';
  }

  private getHiddenMarginTop(): string {
    return environment.mobile ? '-62px' : '-39px';
  }

  private getHiddenMarginBottom(): string {
    return '0';
  }

}
